/**
 * Sleep API 타입 및 Hooks
 * @MP/shared/api/hooks/sleepApi에서 타입과 hooks를 모두 import할 수 있도록 함
 */

// 타입 re-export
export type { SleepDiary } from '../generated/survey/model/sleep-diary';
export type { SleepSession } from '../generated/survey/model/sleep-session';
export type { SleepSessionCreateRequest } from '../generated/survey/model/sleep-session-create-request';
export type { Result as SleepResult } from '../generated/survey/model/result';
export type { SleepSessionSummaryRow } from '../generated/survey/model/sleep-session-summary-row';
export { SleepSessionStatus } from '../generated/survey/model/sleep-session-status';
// Hooks 구현
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// 중복 접수(더블클릭·연타) 방지: 발송/생성 mutation 은 진행 중이면 새 요청 안 보내고 진행중 Promise 반환.
import { useGuardedMutation as useMutation } from '../../hooks/useGuardedMutation';
import { API_CONFIG } from '../config/env';
import {
  CancellationRequest,
  SleepSession,
  SleepSessionControllerApi,
  SleepSessionCreateRequest,
  SleepSessionSummaryRow,
} from '../generated/survey';
import { QK_DASHBOARD_SLEEP_SUMMARY, QK_SLEEP_DIARY_LIST, QK_SLEEP_SESSION_LIST } from '../query-keys';

const SURVEY_URL = API_CONFIG.SURVEY_URL;
const sleepControllerApi = new SleepSessionControllerApi(undefined, SURVEY_URL);

/**
 * org 단위 수면일기 현황(GET /api/v1/sleep-sessions/summary).
 * 발송예정·진행중은 전부, 완료·취소는 최근 7일 이내만 온다. 기관은 토큰에서 읽는다.
 * 환자 이름·차트번호·성별은 실려 오지 않으므로 호출부가 환자 목록과 patientId 로 조합해야 한다.
 */
export const useFetchSleepSessionSummary = () =>
  useQuery<SleepSessionSummaryRow[], AxiosError>({
    queryKey: [QK_DASHBOARD_SLEEP_SUMMARY],
    queryFn: () => sleepControllerApi.getSummary().then((res) => res.data),
    // 이용 현황은 항상 최신 상태를 조회한다(캐시 미사용).
    gcTime: 0,
    staleTime: 0,
  });

/**
 * 세션 목록 정렬 기준(최신 우선). 호출부는 첫 항목을 '최신 세션' 으로 쓰므로 서버 응답 순서에 기대지 않는다.
 * createdTime 은 스펙상 필수지만 아직 안 내려주는 서버가 있어(dev) 없으면 시작일로 대신한다.
 */
const createdAtOf = (session: SleepSession): number =>
  new Date(session.createdTime ?? session.startDate).getTime();

/**
 * placeholderData=false 면 전역 기본값의 keepPreviousData 를 끈다.
 * 켜두면 환자를 바꾼 직후 '이전 환자의 세션' 이 data 로 들어오면서 isLoading 이 false 가 된다.
 */
export const useFetchSleepSessions = (patientId?: string, placeholderData: boolean = true) => {
  return useQuery({
    queryKey: [QK_SLEEP_SESSION_LIST, patientId],
    queryFn: () =>
      sleepControllerApi
        .getSleepSessions(patientId!!)
        .then((res) => [...res.data].sort((a, b) => createdAtOf(b) - createdAtOf(a))),
    enabled: !!patientId,
    // 환자가 앱 밖에서 응답한 결과라 갱신 신호가 없다 — 전역 기본 캐시(3시간)를 끈다.
    gcTime: 0,
    staleTime: 0,
    ...(placeholderData === false && { placeholderData: undefined }),
  });
};

export const useFetchSleepDiaries = (sessionId?: string, placeholderData: boolean = true) => {
  return useQuery({
    queryKey: [QK_SLEEP_DIARY_LIST, sessionId],
    queryFn: () =>
      sleepControllerApi.getSleepDiaries(sessionId!!).then((res) => res.data.sort((a, b) => a.sequence - b.sequence)),
    enabled: !!sessionId,
    // 환자가 앱 밖에서 작성한 기록이라 갱신 신호가 없다 — 전역 기본 캐시(3시간)를 끈다.
    gcTime: 0,
    staleTime: 0,
    ...(placeholderData === false && { placeholderData: undefined }),
  });
};

export const useCreateSleepSession = () => {
  const client = useQueryClient();
  return useMutation<any, AxiosError, SleepSessionCreateRequest>({
    mutationFn: async (body) => sleepControllerApi.createSleepSession(body).then((res) => res.data),
    onSuccess: (_, body) => client.invalidateQueries({ queryKey: [QK_SLEEP_SESSION_LIST, body.patientId] }),
  });
};

export const useCancelSleepSession = (patientId: string, sessionId: string) => {
  const client = useQueryClient();
  return useMutation<any, AxiosError, CancellationRequest>({
    mutationFn: async (body) => sleepControllerApi.cancelSleepSession(sessionId, body).then((res) => res.data),
    onSuccess: (_, body) => client.invalidateQueries({ queryKey: [QK_SLEEP_SESSION_LIST, patientId] }),
  });
};
